<script setup>
const { scholarship } = defineProps({
  scholarship: Object,
});

const allUniqueDegrees = computed(() => {
  if (!scholarship?.programs) return "";

  const uniqueDegrees = new Set(
    scholarship.programs
      .map(
        (program) =>
          program.attributes?.data?.find((attr) => attr.key === "degree")
            ?.value,
      )
      .filter(Boolean),
  );

  return Array.from(uniqueDegrees).join(", ");
});
</script>

<template>
  <!-- Desktop Card -->
  <div
    class="hidden md:flex rounded-lg border border-slate-200 bg-white p-6 items-start gap-8 hover:shadow cursor-pointer h-full"
    @click="navigateTo(`/scholarships/${scholarship?.slug}`)"
  >
    <div>
      <Avatar
        v-if="!getUniversityLogo(scholarship?.university?.media)"
        icon="pi pi-graduation-cap"
        size="xlarge"
        class="w-24 h-24"
      />
      <img
        v-else
        :src="getUniversityLogo(scholarship?.university?.media)"
        alt="University logo"
        class="size-24 object-cover"
      />
    </div>
    <div class="space-y-6 flex-1">
      <div>
        <h1 class="text-xl cursor-pointer font-semibold text-primary-600">
          {{ scholarship?.name }}
        </h1>
        <!-- <p class="text-muted text-xs">{{ scholarship?.university.name }}</p> -->
        <p
          class="hover:underline cursor-pointer text-gray-800 text-sm"
          @click="
            navigateTo(`/universities/${scholarship?.university?.slug}`, {
              external: true,
              open: { target: '_blank' },
            })
          "
        >
          {{ scholarship?.university?.name }}
        </p>
      </div>
      <div class="grid grid-cols-2 gap-10 text-body">
        <div>
          <p class="text-sm">Eligible program</p>
          <p
            v-if="scholarship?.programs?.length === 0"
            class="text-base text-gray-600"
          >
            No programs
          </p>
          <p v-else class="text-lg font-bold text-primary-500">
            {{ scholarship?.programs?.length }}
          </p>
        </div>
        <div>
          <p class="text-sm">Degree</p>
          <p class="text-lg font-bold text-primary-500" v-if="allUniqueDegrees">
            {{ allUniqueDegrees }}
          </p>
          <p class="text-base text-gray-600" v-else>Not provided</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile Card -->
  <div
    class="block md:hidden rounded-lg border border-slate-200 bg-white px-6 py-4 hover:shadow cursor-pointer h-full"
    @click="navigateTo(`/scholarships/${scholarship?.slug}`)"
  >
    <div class="flex items-center gap-4 mb-4">
      <Avatar
        v-if="!getUniversityLogo(scholarship?.university?.media)"
        icon="pi pi-graduation-cap"
        size="large"
        class="!shrink-0 w-16 h-16"
      />
      <img
        v-else
        :src="getUniversityLogo(scholarship?.university?.media)"
        alt="University logo"
        class="!shrink-0 w-16 h-16 object-cover rounded"
      />
      <div class="space-y-1">
        <h1
          class="text-base lg:text-xl font-semibold underline-offset-4 text-primary-600"
        >
          {{ scholarship?.name }}
        </h1>
        <NuxtLink
          :to="`/universities/${scholarship?.university?.slug}`"
          class="text-primary-600 font-medium text-sm hover:underline"
          >{{ scholarship?.university?.name }}
        </NuxtLink>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-body">
      <div>
        <p class="text-xs">Eligible program</p>
        <p
          v-if="scholarship?.programs?.length === 0"
          class="italic text-muted text-sm"
        >
          No programs
        </p>
        <p v-else class="text-sm font-bold text-primary-500">
          {{ scholarship?.programs?.length }}
        </p>
      </div>
      <div>
        <p class="text-xs">Degree</p>
        <p class="text-sm font-bold text-primary-500" v-if="allUniqueDegrees">
          {{ allUniqueDegrees }}
        </p>
        <p class="text-muted italic text-sm" v-else>Not provided</p>
      </div>
    </div>
  </div>
</template>
